import autobind from "autobind-decorator";
import type React from "react";
import type { ReactNode } from "react";
import { Col, Form, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import type { OverlayInjectedProps } from "react-bootstrap/esm/Overlay";
import type { FieldRenderProps } from "react-final-form";
import type { CommonOption } from "@/shared/ui";
import { __ } from "@/shared/ui";
import { FormInput } from "./FormInput";
import type { FormItemProps } from "./FormItem";

export interface FormCheckOption<Type = string, LabelType extends string | ReactNode = string>
	extends CommonOption<Type, LabelType> {
	id?: string;
	infoTooltip?: ReactNode;
	nestedOptions?: FormCheckOptions<Type>;
}

export type FormCheckOptions<Type = string, LabelType extends string | ReactNode = string> = FormCheckOption<
	Type,
	LabelType
>[];

export interface FormCheckListProps<Type = string> extends FormItemProps<Type[]> {
	/**
	 * Options for the check list.
	 */
	options: FormCheckOptions<Type, ReactNode>;
	/**
	 * Whether to include a "Select All" option.
	 */
	selectAllOption?: boolean;
	/**
	 * Optional CSS class applied to each option container.
	 * @default "col-12"
	 */
	optionClassName?: string;
}

export class FormCheckList<T = string> extends FormInput<T[], FormCheckListProps<T>> {
	protected renderInput(fieldProps: FieldRenderProps<T[]>, _additionalClass?: string): ReactNode {
		let {
			input: { value = [] },
		} = fieldProps;

		const {
			input: { name, onChange },
		} = fieldProps;
		const { options = [], selectAllOption } = this.props;

		if (!Array.isArray(value)) {
			onChange([]);
			value = [];
		}

		if (this.props.inline) {
			return (
				<div className="d-flex">
					{Array.isArray(options) &&
						options.map((option: FormCheckOption<T>, key) => (
							<div className="ml-2" key={`inline_${name}_${key}`}>
								{this.renderChecbox(option, fieldProps, value, key)}
							</div>
						))}
					{selectAllOption && this.renderSelectAllCheckbox(fieldProps, value)}
				</div>
			);
		}

		return (
			<Row>
				{Array.isArray(options) &&
					options.map((option: FormCheckOption<T>, key) =>
						this.renderChecbox(option, fieldProps, value, key),
					)}
				{selectAllOption && <Col xs={12}>{this.renderSelectAllCheckbox(fieldProps, value)}</Col>}
			</Row>
		);
	}

	private renderChecbox(
		option: FormCheckOption<T>,
		fieldProps: FieldRenderProps<T[]>,
		value: T[],
		key: number,
	): ReactNode {
		const { infoTooltip } = option;
		const {
			input: { name },
		} = fieldProps;

		if (infoTooltip) {
			return (
				<OverlayTrigger
					key={`overlay-trigger-${name}-${key}`}
					overlay={(props: OverlayInjectedProps) => (
						<Tooltip id={`infoTooltip-${name}-${key}`} className="tooltip-primary" {...props}>
							{infoTooltip}
						</Tooltip>
					)}
				>
					<div className="d-inline">{this.renderChecboxField(option, fieldProps, value, key)}</div>
				</OverlayTrigger>
			);
		}

		return this.renderChecboxField(option, fieldProps, value, key);
	}

	private renderChecboxField(
		option: FormCheckOption<T>,
		fieldProps: FieldRenderProps<T[]>,
		value: T[],
		key: number,
	): ReactNode {
		const { readOnly, tabIndex, optionClassName = "col-12" } = this.props;
		const {
			input: { name },
		} = fieldProps;

		const { disabled } = this.props;

		return (
			<Col className={optionClassName} key={`col_${name}_${key}`}>
				<Form.Check
					readOnly={readOnly}
					disabled={disabled || readOnly || option?.disabled}
					tabIndex={tabIndex}
					onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
						const set = new Set<T>(value);

						if (event.currentTarget.checked) {
							set.add(option.value);
						} else {
							set.delete(option.value);
						}

						this.handleChange(Array.from(set), fieldProps);
					}}
					onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
						this.handleFocus(event, fieldProps);
					}}
					onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
						this.handleBlur(event, fieldProps);
					}}
					custom
					type="checkbox"
					name={name}
					id={`${this.id}-${option.value}`}
					value={String(option.value)}
					label={option.label}
					checked={value.includes(option.value)}
				/>

				{option.nestedOptions?.map((nestedOption: FormCheckOption<T>, nestedKey) =>
					this.renderChecbox(nestedOption, fieldProps, value, nestedKey),
				)}
			</Col>
		);
	}

	private renderSelectAllCheckbox(fieldProps: FieldRenderProps<T[]>, value: T[]): ReactNode {
		const { readOnly, disabled, tabIndex, options = [] } = this.props;
		const {
			input: { name },
		} = fieldProps;
		const notDisabledOptions = Array.isArray(options) ? options.filter((option) => !option?.disabled) : [];
		const allOptionIsDisabled = notDisabledOptions.length === 0;

		return (
			<Form.Check
				className="font-weight-bold"
				readOnly={readOnly}
				disabled={disabled || readOnly || allOptionIsDisabled}
				tabIndex={tabIndex}
				onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
					if (event.currentTarget.checked) {
						this.handleChange(
							options.filter((option) => !option.disabled).map((option) => option.value),
							fieldProps,
						);
					} else {
						this.handleChange([], fieldProps);
					}
				}}
				onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
					this.handleFocus(event, fieldProps);
				}}
				onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
					this.handleBlur(event, fieldProps);
				}}
				custom
				type="checkbox"
				name={name}
				id={`${this.id}-select-all`}
				value="select-all"
				label={__("Összes kijelölése")}
				checked={
					notDisabledOptions.length > 0 && !notDisabledOptions.find((option) => !value.includes(option.value))
				}
				// indeterminate={!!options.find(option => value.includes(option.value))}
			/>
		);
	}

	@autobind
	protected handleValidate(value: T[], allValues: Record<string, any>): string {
		const { required, validate } = this.props;

		if (this.isDisabled()) {
			return undefined;
		}

		if (required && (!Array.isArray(value) || value.length === 0)) {
			return "Kérjük válassz ki egy elemet!";
		}

		if (validate) {
			return validate(value, allValues);
		}
	}
}
